import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Loader2, ShieldCheck, ShieldX, Inbox } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API = `${window.__BACKEND_URL__}/api`;

/**
 * Approvals inbox — modal launched from Manage Teams. Lets a peer
 * admin see role-change requests waiting on them (for_me), the ones
 * they kicked off (mine), and the company-wide pending queue (all).
 */
export const ApprovalsInbox = ({ open, onOpenChange, token, currentUserId }) => {
  const [tab, setTab] = useState('for_me');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [noteByRequest, setNoteByRequest] = useState({});

  const fetchItems = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const status = tab === 'mine' ? 'all' : 'pending';
      const r = await axios.get(`${API}/admin/role-change-requests`, {
        params: { scope: tab, status },
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(r.data.items || []);
    } catch (e) {
      toast({ title: 'Failed to load approvals', description: e.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [tab, open, token]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const act = async (requestId, action) => {
    setActionLoading((s) => ({ ...s, [requestId]: action }));
    try {
      await axios.post(
        `${API}/admin/role-change-requests/${requestId}/${action}`,
        { note: noteByRequest[requestId] || undefined },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({ title: action === 'approve' ? 'Approved' : action === 'reject' ? 'Rejected' : 'Cancelled' });
      await fetchItems();
    } catch (e) {
      toast({ title: 'Action failed', description: e.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setActionLoading((s) => ({ ...s, [requestId]: null }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            <Inbox className="w-6 h-6 text-blue-500" />
            Approvals
          </DialogTitle>
          <DialogDescription className="pt-2">
            Role changes made by an admin or super-user apply immediately — no second admin approval is required (single-admin rule, Jun 2026). This inbox only holds any older pending requests from the retired two-admin workflow.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="for_me" data-testid="approvals-tab-for-me">For me</TabsTrigger>
            <TabsTrigger value="mine" data-testid="approvals-tab-mine">Mine</TabsTrigger>
            <TabsTrigger value="all" data-testid="approvals-tab-all">All pending</TabsTrigger>
          </TabsList>

          {['for_me', 'mine', 'all'].map((t) => (
            <TabsContent key={t} value={t} className="space-y-3 pt-3">
              {loading && (
                <div className="flex items-center gap-2 text-blue-300">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
              )}
              {!loading && items.length === 0 && (
                <div className="text-sm text-slate-400 italic" data-testid={`approvals-empty-${t}`}>
                  Nothing here right now.
                </div>
              )}
              {!loading && items.map((req) => {
                const isOwn = req.requester_id === currentUserId;
                const alreadyDecided = (req.approvals || []).some((a) => a.actor_id === currentUserId);
                const canApprove = !isOwn && !alreadyDecided && req.status === 'pending';
                return (
                  <div key={req.id} className="rounded-lg border-2 border-slate-700 bg-slate-900/50 p-4 space-y-3" data-testid={`approval-row-${req.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold text-white">
                          {req.target_user_name || req.target_user_email}
                          <span className="text-slate-400 font-normal mx-2">
                            {req.current_role} → <span className="text-emerald-300">{req.requested_role}</span>
                          </span>
                        </div>
                        <div className="text-xs text-slate-400 mt-1">
                          Requested by {req.requester_name} on {new Date(req.created_at).toLocaleString()}
                          {req.reason && <span className="italic"> — &quot;{req.reason}&quot;</span>}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          Approvals so far: {(req.approvals || []).map((a) => a.actor_name).join(', ') || 'None'}
                        </div>
                      </div>
                      <span className={`text-xs uppercase tracking-wide font-semibold rounded px-2 py-0.5 shrink-0 ${
                        req.status === 'pending' ? 'bg-amber-900/40 text-amber-200'
                        : req.status === 'approved' ? 'bg-emerald-900/40 text-emerald-200'
                        : req.status === 'rejected' ? 'bg-rose-900/40 text-rose-200'
                        : 'bg-slate-800 text-slate-300'
                      }`}>{req.status}</span>
                    </div>

                    {canApprove && (
                      <>
                        <Textarea
                          placeholder="Add a note (optional)"
                          value={noteByRequest[req.id] || ''}
                          onChange={(e) => setNoteByRequest((s) => ({ ...s, [req.id]: e.target.value }))}
                          className="text-xs"
                          data-testid={`approval-note-${req.id}`}
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => act(req.id, 'approve')}
                            disabled={!!actionLoading[req.id]}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid={`approval-approve-${req.id}`}
                          >
                            {actionLoading[req.id] === 'approve' ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3 mr-1" />}
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => act(req.id, 'reject')}
                            disabled={!!actionLoading[req.id]}
                            data-testid={`approval-reject-${req.id}`}
                          >
                            {actionLoading[req.id] === 'reject' ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldX className="w-3 h-3 mr-1" />}
                            Reject
                          </Button>
                        </div>
                      </>
                    )}
                    {isOwn && req.status === 'pending' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => act(req.id, 'cancel')}
                        disabled={!!actionLoading[req.id]}
                        data-testid={`approval-cancel-${req.id}`}
                      >
                        Cancel my request
                      </Button>
                    )}
                  </div>
                );
              })}
            </TabsContent>
          ))}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default ApprovalsInbox;
